import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef
} from 'react';
import {
  User,
  Project,
  Task,
  Notification as AppNotification,
  TimelineEvent,
  TimelineEventType
} from '../types';
import { supabase, db } from '../services';
import { useAuth } from './AuthContext';
import { generateId } from '../utils/formatters';
import { useAppVisibility } from '../hooks/useAppVisibility';

// ============================================
// Types
// ============================================

interface ProjectContextType {
  // Data
  users: User[];
  projects: Project[];
  tasks: Task[];
  notifications: AppNotification[];
  timeline: TimelineEvent[];

  // Setters (for optimistic updates)
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setNotifications: React.Dispatch<React.SetStateAction<AppNotification[]>>;
  setTimeline: React.Dispatch<React.SetStateAction<TimelineEvent[]>>;

  // Actions
  addNotification: (title: string, message: string, userId: string, link?: string, sendPush?: boolean) => Promise<void>;
  addTimelineEvent: (projectId: string, type: TimelineEventType, details: string) => Promise<void>;
  refreshAll: () => Promise<void>;

  // Loading state
  isDataLoading: boolean;
}

// ============================================
// Context
// ============================================

const ProjectContext = createContext<ProjectContextType | null>(null);

// ============================================
// Provider
// ============================================

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { isVisible, wasHidden, resetWasHidden } = useAppVisibility();

  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Track the realtime channel for cleanup and reconnection
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const lastRefreshTimeRef = useRef<number>(Date.now());
  const subscriptionRetryCountRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Generate a unique channel ID per tab instance to prevent conflicts
  const tabIdRef = useRef<string>(`tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);

  // Track channel connection status to trigger auto-reconnection
  const [channelStatus, setChannelStatus] = useState<string>('disconnected');

  // Refresh all data from Supabase
  const refreshAll = useCallback(async () => {
    try {
      console.log('[ProjectContext] Refreshing all data...');
      const [u, p, t] = await Promise.all([
        db.getUsers(),
        db.getProjects(),
        db.getTasks(),
      ]);
      setUsers(u);
      setProjects(p);
      setTasks(t);

      if (currentUser) {
        const [n, tl] = await Promise.all([
          db.getNotifications(currentUser.id),
          db.getTimeline(),
        ]);
        setNotifications(n);
        setTimeline(tl);
      }

      lastRefreshTimeRef.current = Date.now();
      console.log('[ProjectContext] Data refresh complete');
    } catch (error) {
      console.error('Supabase data refresh error:', error);
    } finally {
      setIsDataLoading(false);
      setIsReconnecting(false);
    }
  }, [currentUser]);

  // Load data when user changes
  useEffect(() => {
    if (currentUser) {
      refreshAll();
    }
  }, [currentUser?.id, refreshAll]);

  // Force reconnection when app wakes from inactivity
  const forceReconnect = useCallback(async () => {
    console.log('[ProjectContext] Force reconnect initiated');

    // First, refresh the auth session to ensure token is valid
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('[ProjectContext] Session refresh failed:', error);
        return;
      }
      if (!session) {
        console.warn('[ProjectContext] No active session during reconnect');
        return;
      }
      console.log('[ProjectContext] Session validated, token expires at:', new Date(session.expires_at! * 1000).toISOString());
    } catch (e) {
      console.error('[ProjectContext] Session check error:', e);
    }

    // Clean up existing channel and force immediate reconnection
    if (channelRef.current) {
      console.log('[ProjectContext] Removing stale channel');
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Reset retry count for fresh reconnection
    subscriptionRetryCountRef.current = 0;

    // Trigger immediate reconnection
    setChannelStatus('connecting');
  }, []);

  // Handle app becoming visible - refresh data to catch any missed updates
  useEffect(() => {
    if (!currentUser) return;

    // Only refresh if app was hidden and is now visible
    if (isVisible && wasHidden) {
      const timeSinceLastRefresh = Date.now() - lastRefreshTimeRef.current;
      const DEBOUNCE_TIME = 500; // Reduced to 500ms for faster recovery

      // Debounce to avoid multiple rapid refreshes
      if (timeSinceLastRefresh > DEBOUNCE_TIME) {
        console.log('[ProjectContext] App became visible after being hidden, initiating recovery...');
        setIsReconnecting(true);

        // First refresh data to catch any updates that happened while app was hidden
        refreshAll();

        // Force aggressive reconnection - don't just check status, force new connection
        // This ensures we recover from stale WebSocket connections
        const channelState = channelRef.current?.state;
        console.log('[ProjectContext] Current channel state:', channelState);

        // Always force reconnection after being hidden for reliability
        // Mobile browsers often close WebSocket connections when app is in background
        forceReconnect();

        // Reset wasHidden flag after processing to prevent repeated refreshes
        resetWasHidden();
      }
    }
  }, [isVisible, wasHidden, currentUser, refreshAll, resetWasHidden, forceReconnect]);

  // Auto-reconnection effect - monitors channel status and recreates when needed
  useEffect(() => {
    if (!currentUser) return;

    // If channel status indicates we need to reconnect
    if (channelStatus === 'reconnecting' || channelStatus === 'error' || channelStatus === 'closed') {
      // Clear any existing reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Calculate retry delay with exponential backoff (start at 100ms, max 5 seconds)
      // Faster initial retry for better recovery after brief disconnects
      const retryDelay = Math.min(100 * Math.pow(2, subscriptionRetryCountRef.current), 5000);

      console.log(`[ProjectContext] Scheduling channel reconnection in ${retryDelay}ms (attempt ${subscriptionRetryCountRef.current + 1})`);

      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('[ProjectContext] Executing scheduled reconnection...');

        // Clean up old channel
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }

        // Trigger reconnection by setting status
        setChannelStatus('connecting');
        subscriptionRetryCountRef.current += 1;
      }, retryDelay);

      return () => {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    }
  }, [channelStatus, currentUser]);

  // Real-time subscriptions - create and manage the channel
  useEffect(() => {
    if (!currentUser) return;

    // Don't create a new channel if one already exists and is connected
    if (channelRef.current && channelRef.current.state === 'joined') {
      console.log('[ProjectContext] Realtime channel already connected, skipping setup');
      return;
    }

    // Only create channel when we need to connect/reconnect
    if (channelStatus !== 'connecting' && channelStatus !== 'disconnected') {
      console.log('[ProjectContext] Channel status is', channelStatus, '- waiting for connecting/disconnected state');
      return;
    }

    console.log('[ProjectContext] Setting up realtime subscriptions for tab:', tabIdRef.current);
    console.log('[ProjectContext] Channel status:', channelStatus);

    const channel = supabase
      .channel(`app-global-sync-${tabIdRef.current}`)
      // Notifications
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${currentUser.id}` }, 
        (payload) => {
          const raw = payload.new as any;
          if (payload.eventType === 'INSERT') {
            const newNotif: AppNotification = {
              id: raw.id,
              userId: raw.user_id,
              title: raw.title,
              message: raw.message,
              isRead: raw.is_read,
              timestamp: Number(raw.timestamp),
              link: raw.link,
            };
            setNotifications(prev => {
              if (prev.some(n => n.id === newNotif.id)) return prev;
              return [newNotif, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            setNotifications(prev => 
              prev.map(n => n.id === raw.id ? { ...n, isRead: raw.is_read } : n)
            );
          } else if (payload.eventType === 'DELETE') {
            setNotifications(prev => prev.filter(n => n.id !== (payload.old as any).id));
          }
        }
      )
      // Tasks
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'tasks' },
        (payload) => {
          console.log('[ProjectContext] Task real-time event:', payload.eventType, payload);
          const raw = payload.new as any;
          if (payload.eventType === 'INSERT') {
            console.log('[ProjectContext] Task INSERT:', raw.id, raw.title);
            const newTask: Task = {
              id: raw.id,
              projectId: raw.project_id,
              title: raw.title,
              description: raw.description,
              status: raw.status,
              assigneeId: raw.assignee_id,
              deadline: raw.deadline,
              estimateHours: raw.estimate_hours,
              priority: raw.priority,
              checklist: raw.checklist || [],
            };
            setTasks(prev => {
              if (prev.some(t => t.id === newTask.id)) {
                console.log('[ProjectContext] Task INSERT ignored - already exists');
                return prev;
              }
              console.log('[ProjectContext] Task INSERT applied');
              return [...prev, newTask];
            });
          } else if (payload.eventType === 'UPDATE') {
            console.log('[ProjectContext] Task UPDATE received:', {
              id: raw.id,
              title: raw.title,
              status: raw.status,
              assigneeId: raw.assignee_id,
              priority: raw.priority,
              estimateHours: raw.estimate_hours
            });
            setTasks(prev => {
              const oldTask = prev.find(t => t.id === raw.id);
              if (oldTask) {
                console.log('[ProjectContext] Task before update:', {
                  id: oldTask.id,
                  status: oldTask.status,
                  title: oldTask.title
                });
              }
              const newTasks = prev.map(t => t.id === raw.id ? {
                ...t,
                title: raw.title,
                description: raw.description,
                status: raw.status,
                assigneeId: raw.assignee_id,
                priority: raw.priority,
                deadline: raw.deadline,
                estimateHours: raw.estimate_hours,
                checklist: raw.checklist || [],
              } : t);
              console.log('[ProjectContext] Task UPDATE applied, new status:', raw.status);
              return newTasks;
            });
          } else if (payload.eventType === 'DELETE') {
            console.log('[ProjectContext] Task DELETE:', (payload.old as any).id);
            setTasks(prev => prev.filter(t => t.id !== (payload.old as any).id));
          }
        }
      )
      // Projects
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'projects' }, 
        (payload) => {
          const raw = payload.new as any;
          if (payload.eventType === 'INSERT') {
            const newProject: Project = {
              id: raw.id,
              name: raw.name,
              description: raw.description,
              ownerId: raw.owner_id,
              memberIds: raw.member_ids || [],
              createdAt: raw.created_at,
              deadline: raw.deadline,
              status: raw.status,
            };
            setProjects(prev => {
              if (prev.some(p => p.id === newProject.id)) return prev;
              return [...prev, newProject];
            });
          } else if (payload.eventType === 'UPDATE') {
            setProjects(prev => prev.map(p => p.id === raw.id ? {
              ...p,
              name: raw.name,
              description: raw.description,
              status: raw.status,
              ownerId: raw.owner_id,
              memberIds: raw.member_ids,
              deadline: raw.deadline,
            } : p));
          } else if (payload.eventType === 'DELETE') {
            setProjects(prev => prev.filter(p => p.id !== (payload.old as any).id));
          }
        }
      )
      // Timeline
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'timeline' }, 
        (payload) => {
          const raw = payload.new as any;
          const newEv: TimelineEvent = {
            id: raw.id,
            projectId: raw.project_id,
            userId: raw.user_id,
            userName: raw.user_name,
            type: raw.action,
            details: raw.details,
            timestamp: Number(raw.timestamp),
          };
          setTimeline(prev => {
            // Avoid duplicates from optimistic update
            if (prev.some(e => e.id === newEv.id)) return prev;
            return [newEv, ...prev];
          });
        }
      )
      // Users - for real-time updates when new users are added
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'users' }, 
        (payload) => {
          const raw = payload.new as any;
          if (payload.eventType === 'INSERT') {
            const newUser: User = {
              id: raw.id,
              email: raw.email,
              name: raw.name,
              role: raw.role,
              avatar: raw.avatar,
            };
            setUsers(prev => {
              if (prev.some(u => u.id === newUser.id)) return prev;
              return [...prev, newUser];
            });
          } else if (payload.eventType === 'UPDATE') {
            setUsers(prev => prev.map(u => u.id === raw.id ? {
              ...u,
              name: raw.name,
              role: raw.role,
              avatar: raw.avatar,
            } : u));
          } else if (payload.eventType === 'DELETE') {
            setUsers(prev => prev.filter(u => u.id !== (payload.old as any).id));
          }
        }
      )
      .subscribe((status) => {
        console.log('[ProjectContext] Realtime subscription status:', status);

        if (status === 'SUBSCRIBED') {
          console.log('[ProjectContext] Successfully subscribed to realtime updates');
          // DON'T call setChannelStatus here - it causes the effect to re-run and cleanup
          subscriptionRetryCountRef.current = 0; // Reset retry count on successful connection
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[ProjectContext] Realtime channel error');
          setChannelStatus('error');
        } else if (status === 'TIMED_OUT') {
          console.error('[ProjectContext] Realtime subscription timed out');
          setChannelStatus('error');
        } else if (status === 'CLOSED') {
          console.log('[ProjectContext] Realtime channel closed');
          setChannelStatus('closed');
          // Channel was closed, clear the ref
          if (channelRef.current === channel) {
            channelRef.current = null;
          }
        }
      });

    // Store the channel reference for reconnection handling
    channelRef.current = channel;

    return () => {
      console.log('[ProjectContext] Cleaning up realtime subscriptions');
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id, channelStatus]); // Added channelStatus to allow reconnection when status changes

  // Periodic health check - verify channel is still alive
  useEffect(() => {
    if (!currentUser) return;

    // Check channel health every 30 seconds
    const healthCheckInterval = setInterval(() => {
      if (channelRef.current) {
        const state = channelRef.current.state;
        console.log('[ProjectContext] Health check - Channel state:', state);

        // If channel is not in a healthy state, trigger reconnection
        if (state !== 'joined' && state !== 'joining') {
          console.warn('[ProjectContext] Channel unhealthy, triggering reconnection');
          setChannelStatus('reconnecting');
        }
      } else {
        // No channel exists, create one
        console.warn('[ProjectContext] No channel found, triggering connection');
        setChannelStatus('connecting');
      }
    }, 30000); // 30 seconds

    return () => {
      clearInterval(healthCheckInterval);
    };
  }, [currentUser]);

  // Send push notification to a specific user via OneSignal
  const sendPushToUser = useCallback(async (
    userId: string,
    title: string,
    message: string,
    url?: string
  ) => {
    // IMPORTANT: Direct API calls to OneSignal from client-side code cause CORS errors
    // and expose API keys in the browser (security risk).
    //
    // Proper approaches:
    // 1. Use OneSignal SDK methods (not REST API) - requires proper SDK initialization
    // 2. Call OneSignal API from a backend server (recommended for production)
    // 3. Use Supabase Edge Functions to proxy the API call
    //
    // For now, we'll rely on in-app notifications only (which work perfectly).
    // Push notifications are a nice-to-have feature, not critical for app functionality.

    console.log('[ProjectContext] Push notifications disabled - using in-app notifications only');
    console.log('[ProjectContext] To enable push: implement server-side OneSignal integration');

    // In-app notifications are already created via addNotification() and work great!
    // Users see notifications in the app's notification center when they log in.
  }, []);

  // Add notification helper - now also sends push notification
  const addNotification = useCallback(async (
    title: string, 
    message: string, 
    userId: string, 
    link?: string,
    sendPush: boolean = true
  ) => {
    const newNotif: AppNotification = {
      id: generateId(),
      userId,
      title,
      message,
      isRead: false,
      timestamp: Date.now(),
      link,
    };
    try {
      await db.addNotification(newNotif);
      
      // Send push notification to the target user if enabled
      if (sendPush && userId !== currentUser?.id) {
        await sendPushToUser(userId, title, message, link);
      }
    } catch (e) {
      console.error('Notification sync error', e);
    }
  }, [currentUser?.id, sendPushToUser]);

  // Add timeline event helper
  const addTimelineEvent = useCallback(async (
    projectId: string, 
    type: TimelineEventType, 
    details: string
  ) => {
    if (!currentUser) return;
    
    const newEvent: TimelineEvent = {
      id: generateId(),
      projectId,
      userId: currentUser.id,
      userName: currentUser.name,
      type,
      details,
      timestamp: Date.now(),
    };
    
    // Optimistic update
    setTimeline(prev => [newEvent, ...prev]);
    
    try {
      await db.addTimelineEvent(newEvent);
    } catch (e) {
      console.error('Timeline event sync error', e);
      // Rollback on error
      setTimeline(prev => prev.filter(ev => ev.id !== newEvent.id));
    }
  }, [currentUser]);

  const value: ProjectContextType = {
    users,
    projects,
    tasks,
    notifications,
    timeline,
    setProjects,
    setTasks,
    setNotifications,
    setTimeline,
    addNotification,
    addTimelineEvent,
    refreshAll,
    isDataLoading,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export { ProjectContext };
export default ProjectContext;
